import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  CheckCircle,
  Download,
  Edit3,
  Users,
  DollarSign,
  TrendingDown,
  Banknote,
  UserCheck,
  Search,
  Pencil,
  Receipt,
  Landmark,
  FileDown,
  AlertTriangle,
} from "lucide-react";
import { Badge, statusVariant } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { Modal } from "../../../components/ui/Modal";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { StatCard } from "../../../components/ui/StatCard";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useAdminPayslips, useBizAdminPayslips } from "../../../hooks/usePayslip";
import { useApprovePayroll, useApproveBizPayroll, useBizPayrollRunStatus } from "../../../hooks/usePayroll";
import { useAuth } from "../../../hooks/useAuth";
import { payslipApi } from "../../../lib/payslipApi";
import type { Payslip } from "../../../lib/payslipApi";
import { formatCurrency, formatDate, formatPeriod } from "../../../utils/format";
import { ProcessPayslipModal } from "./ProcessPayslipModal";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDeductionAmount(deductions: Payslip["deductions"], keyword: string): number {
  return deductions.find((d) => d.label.toLowerCase().includes(keyword.toLowerCase()))?.amount ?? 0;
}

function resolveName(payslip: Payslip): string {
  return payslip.employeeName ?? payslip.userId;
}

// ── Summary Cards ─────────────────────────────────────────────────────────────

interface SummaryData {
  totalEmployees: number;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
  totalGovDed: number;
  totalLoanDed: number;
  totalTax: number;
  totalAdjustments: number;
  totalEmployerContributions: number;
  draftCount: number;
  finalizedCount: number;
}

function computeSummary(payslips: Payslip[]): SummaryData {
  return {
    totalEmployees: payslips.length,
    totalGross: payslips.reduce((s, p) => s + p.grossPay, 0),
    totalDeductions: payslips.reduce((s, p) => s + (p.grossPay - p.netPay), 0),
    totalNet: payslips.reduce((s, p) => s + p.netPay, 0),
    totalGovDed: payslips.reduce(
      (s, p) =>
        s +
        getDeductionAmount(p.deductions, "SSS") +
        getDeductionAmount(p.deductions, "PhilHealth") +
        getDeductionAmount(p.deductions, "Pag-IBIG"),
      0,
    ),
    totalLoanDed: payslips.reduce(
      (s, p) => s + p.loanDeductions.reduce((ls, d) => ls + d.amount, 0),
      0,
    ),
    totalTax: payslips.reduce(
      (s, p) => s + getDeductionAmount(p.deductions, "tax"),
      0,
    ),
    totalAdjustments: payslips.reduce(
      (s, p) => s + (p.ppaAdjustments ?? []).reduce((as, a) => as + a.amount, 0),
      0,
    ),
    totalEmployerContributions: payslips.reduce(
      (s, p) => s + (p.employerContributions ?? []).reduce((es, c) => es + c.amount, 0),
      0,
    ),
    draftCount: payslips.filter((p) => p.status === "DRAFT").length,
    finalizedCount: payslips.filter((p) => p.status === "FINALIZED").length,
  };
}

// ── CSV Export ────────────────────────────────────────────────────────────────

function escapeCSV(value: string | number): string {
  const s = String(value);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function exportPayslipsToCSV(payslips: Payslip[], period: string) {
  const headers = [
    "Employee",
    "Basic Pay",
    "Gross Pay",
    "SSS",
    "Absences",
    "Payroll Adjustment",
    "Incentives",
    "Cash Advance",
    "Loan Deductions",
    "OT / Holiday",
    "Net Pay",
    "Status",
    "Finalized By",
    "Finalized At",
  ];

  const rows = payslips.map((p) => {
    const sss = getDeductionAmount(p.deductions, "SSS");
    const absence = getDeductionAmount(p.deductions, "Absence");
    const adjustments = (p.ppaAdjustments ?? []).reduce((s, a) => s + a.amount, 0);
    const incentives = (p.incentives ?? []).reduce((s, d) => s + d.amount, 0);
    const cashAdv = (p.cashAdvanceDeductions ?? []).reduce((s, d) => s + d.amount, 0);
    const loanDed = p.loanDeductions.reduce((s, d) => s + d.amount, 0);
    const workAdj = (p.workAdjustments ?? []).reduce((s, d) => s + d.amount, 0);

    return [
      p.employeeName ?? p.userId,
      p.basicPay,
      p.grossPay,
      sss,
      absence,
      adjustments,
      incentives,
      cashAdv,
      loanDed,
      workAdj,
      p.netPay,
      p.status,
      p.finalizedBy ?? "",
      p.finalizedAt ? new Date(p.finalizedAt).toLocaleString() : "",
    ].map(escapeCSV).join(",");
  });

  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payslips-${period}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ProcessedPayslipsPage() {
  // Route can be either /:period/results (super-admin) or /:businessId/:period/results (biz admin)
  const { period = "", businessId: routeBusinessId } = useParams<{
    period: string;
    businessId?: string;
  }>();
  const navigate = useNavigate();
  const { toasts, toast, dismissToast } = useToast();
  const { isActiveBusinessAdmin, activeBusinessId } = useAuth();

  // Resolve the businessId: prefer the route param, fall back to active business from context.
  const resolvedBusinessId = routeBusinessId ?? activeBusinessId ?? "";
  // Use biz-scoped path whenever a business is selected (business admin or super-admin with active biz).
  const useBizScoped = isActiveBusinessAdmin || !!routeBusinessId || !!resolvedBusinessId;

  // Always call both hooks; enable only the relevant one (React hooks rules).
  const adminApprove = useApprovePayroll();
  const bizApprove = useApproveBizPayroll(resolvedBusinessId);
  const approvePayroll = useBizScoped ? bizApprove : adminApprove;

  const adminPayslipsQuery = useAdminPayslips(period, { enabled: !useBizScoped });
  const bizPayslipsQuery = useBizAdminPayslips(resolvedBusinessId, period);
  const { data, isLoading, refetch: refetchPayslips } = useBizScoped
    ? bizPayslipsQuery
    : adminPayslipsQuery;

  const { data: payrollRun } = useBizPayrollRunStatus(resolvedBusinessId, period);
  const missingEmployees = payrollRun?.missingEmployees ?? [];
  const [missingDismissed, setMissingDismissed] = useState(false);

  const [search, setSearch] = useState("");
  const [overrideTarget, setOverrideTarget] = useState<{ userId: string; name: string } | null>(null);
  const [confirmApprove, setConfirmApprove] = useState(false);

  const payslips = data?.items ?? [];

  const summary = useMemo(() => computeSummary(payslips), [payslips]);
  const periodLabel = formatPeriod(period);
  const allFinalized = summary.draftCount === 0 && summary.totalEmployees > 0;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return payslips;
    return payslips.filter((p) => {
      const name = (p.employeeName ?? p.userId).toLowerCase();
      return name.includes(q);
    });
  }, [payslips, search]);

  const handleApproveAll = async () => {
    setConfirmApprove(false);
    try {
      const result = await approvePayroll.mutateAsync(period);
      toast.success(`${result.finalized} payslip${result.finalized !== 1 ? "s" : ""} approved and finalized.`);
      refetchPayslips();
    } catch {
      toast.error("Failed to approve payslips. Please try again.");
    }
  };

  const handleDownload = async (payslip: Payslip) => {
    try {
      let url: string;
      if (useBizScoped && resolvedBusinessId) {
        ({ url } = await payslipApi.bizAdminDownload(
          resolvedBusinessId,
          payslip.userId,
          payslip.period,
        ));
      } else {
        ({ url } = await payslipApi.adminDownload(payslip.userId, payslip.period, resolvedBusinessId || undefined));
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Failed to generate PDF.");
    }
  };

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            onClick={() => navigate("/admin/payslip")}
            className="flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#2447d9] mb-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Process Payslip
          </button>
          <h1 className="text-2xl font-bold text-[#0f172a]">
            Processed Payslips — {periodLabel}
          </h1>
          <p className="mt-1 text-sm text-[#64748b]">
            Review and approve payslips. Override individual entries before approving.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          {payslips.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => exportPayslipsToCSV(payslips, period)}
            >
              <FileDown className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          )}
          {!allFinalized && (
            <Button
              variant="primary"
              onClick={() => setConfirmApprove(true)}
              disabled={summary.totalEmployees === 0 || approvePayroll.isPending}
            >
              <CheckCircle className="h-4 w-4 mr-1.5" />
              {approvePayroll.isPending ? "Approving…" : "Approve All Payslips"}
            </Button>
          )}
          {allFinalized && (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16a34a]">
              <CheckCircle className="h-4 w-4" />
              All payslips approved
            </span>
          )}
        </div>
      </div>

      {/* Status bar */}
      {summary.totalEmployees > 0 && (
        <div className="flex items-center gap-3 text-sm rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-2.5">
          <span className="text-[#0f172a] font-medium">{summary.totalEmployees} employees</span>
          <span className="text-[#94a3b8]">·</span>
          <span className="text-[#16a34a] font-medium">{summary.finalizedCount} finalized</span>
          <span className="text-[#94a3b8]">·</span>
          <span className="text-[#d97706] font-medium">{summary.draftCount} pending approval</span>
        </div>
      )}

      {/* Missing employees warning */}
      {missingEmployees.length > 0 && !missingDismissed && (
        <div className="flex items-start gap-3 rounded-xl border border-[#fde68a] bg-[#fffbeb] px-4 py-3.5">
          <AlertTriangle className="h-5 w-5 text-[#d97706] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#92400e]">
              {missingEmployees.length} employee{missingEmployees.length !== 1 ? "s" : ""} were in the previous payroll but are missing from this run
            </p>
            <p className="text-xs text-[#92400e] mt-0.5 mb-2">
              They were not marked as excluded. Check their profile status or membership for this business.
            </p>
            <div className="flex flex-wrap gap-2">
              {missingEmployees.map((e) => (
                <button
                  key={e.userId}
                  onClick={() => navigate(`/admin/users/${e.userId}`)}
                  className="inline-flex items-center gap-1 rounded-full border border-[#fde68a] bg-white px-2.5 py-0.5 text-xs font-medium text-[#92400e] hover:bg-[#fef3c7] transition-colors"
                >
                  {e.name ?? e.userId}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setMissingDismissed(true)}
            className="shrink-0 text-[#d97706] hover:text-[#92400e] transition-colors text-lg leading-none"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {!isLoading && payslips.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            title="Total Gross Pay"
            value={formatCurrency(summary.totalGross)}
            icon={<DollarSign className="h-5 w-5 text-[#2447d9]" />}
            iconBgClass="bg-[#eff3ff] text-[#2447d9]"
          />
          <StatCard
            title="Net Disbursement"
            value={formatCurrency(summary.totalNet)}
            icon={<Banknote className="h-5 w-5 text-[#16a34a]" />}
            iconBgClass="bg-[#f0fdf4] text-[#16a34a]"
          />
          <StatCard
            title="Gov. Contributions"
            value={formatCurrency(summary.totalGovDed)}
            icon={<TrendingDown className="h-5 w-5 text-[#d97706]" />}
            iconBgClass="bg-[#fffbeb] text-[#d97706]"
          />
          <StatCard
            title="Loan Deductions"
            value={formatCurrency(summary.totalLoanDed)}
            icon={<Users className="h-5 w-5 text-[#64748b]" />}
            iconBgClass="bg-[#f8fafc] text-[#64748b]"
          />
          <StatCard
            title="Total Deductions"
            value={formatCurrency(summary.totalDeductions)}
            icon={<Receipt className="h-5 w-5 text-[#dc2626]" />}
            iconBgClass="bg-[#fef2f2] text-[#dc2626]"
          />
          {summary.totalTax > 0 && (
            <StatCard
              title="Total Tax Withheld"
              value={formatCurrency(summary.totalTax)}
              icon={<Landmark className="h-5 w-5 text-[#7c3aed]" />}
              iconBgClass="bg-[#f5f3ff] text-[#7c3aed]"
            />
          )}
          {summary.totalEmployerContributions > 0 && (
            <StatCard
              title="Employer Contributions"
              value={formatCurrency(summary.totalEmployerContributions)}
              icon={<Building2 className="h-5 w-5 text-[#0891b2]" />}
              iconBgClass="bg-[#ecfeff] text-[#0891b2]"
            />
          )}
        </div>
      )}

      {/* Payslips Table */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-[#0f172a] shrink-0">Employee Payslips</h2>
          {payslips.length > 0 && (
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by employee name…"
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] pl-9 pr-3 py-1.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
            </div>
          )}
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {!isLoading && payslips.length === 0 && (
          <EmptyState
            icon={<Users className="h-8 w-8 text-[#94a3b8]" />}
            title="No payslips for this period"
            description="Go back and process the payroll first."
          />
        )}

        {!isLoading && payslips.length > 0 && filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-[#64748b]">
            No employees match <strong>{search}</strong>.
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1250px]">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  {[
                    "Employee",
                    "Basic Pay",
                    "Gross Pay",
                    "SSS",
                    "Absences",
                    "Payroll Adj.",
                    "Incentives",
                    "Cash Advance",
                    "Loan Ded.",
                    "OT / Holiday",
                    "Net Pay",
                    "Status",
                    "Audit",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f5f9]">
                {filtered.map((payslip) => {
                  const name = resolveName(payslip);
                  const sss = getDeductionAmount(payslip.deductions, "SSS");
                  const absence = getDeductionAmount(payslip.deductions, "Absence");
                  const totalAdjustments = (payslip.ppaAdjustments ?? []).reduce((s, a) => s + a.amount, 0);
                  const totalIncentives = (payslip.incentives ?? []).reduce((s, d) => s + d.amount, 0);
                  const cashAdv = (payslip.cashAdvanceDeductions ?? []).reduce((s, d) => s + d.amount, 0);
                  const totalLoan = payslip.loanDeductions.reduce((s, d) => s + d.amount, 0);
                  const totalWorkAdj = (payslip.workAdjustments ?? []).reduce((s, d) => s + d.amount, 0);
                  const wasOverridden = !!payslip.processedBy;
                  const pd = payslip.prorationDetails;

                  return (
                    <tr
                      key={payslip.userId}
                      className="bg-white hover:bg-[#f8fafc] transition-colors"
                    >
                      {/* Employee name */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-[#0f172a]">{name}</div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {payslip.isProrated && pd && (
                                <span
                                  className="inline-flex items-center gap-0.5 text-[10px] font-medium text-[#d97706]"
                                  title={`Hire date: ${pd.hireDate} — ${pd.workedDays}/${pd.totalDays} days`}
                                >
                                  <CalendarClock className="h-2.5 w-2.5" />
                                  Prorated ({pd.workedDays}/{pd.totalDays}d)
                                </span>
                              )}
                              {wasOverridden && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-[#d97706]">
                                  <Pencil className="h-2.5 w-2.5" />
                                  Overridden
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#0f172a]">
                        {formatCurrency(payslip.basicPay)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-[#0f172a]">
                        {formatCurrency(payslip.grossPay)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#64748b]">
                        {sss > 0 ? `- ${formatCurrency(sss)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#dc2626]">
                        {absence > 0 ? `- ${formatCurrency(absence)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#7c3aed]">
                        {totalAdjustments > 0 ? `- ${formatCurrency(totalAdjustments)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#16a34a]">
                        {totalIncentives > 0 ? `+ ${formatCurrency(totalIncentives)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#0891b2]">
                        {cashAdv > 0 ? `- ${formatCurrency(cashAdv)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#dc2626]">
                        {totalLoan > 0 ? `- ${formatCurrency(totalLoan)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#d97706]">
                        {totalWorkAdj > 0 ? `+ ${formatCurrency(totalWorkAdj)}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-bold text-[#2447d9]">
                        {formatCurrency(payslip.netPay)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={statusVariant(payslip.status)}>
                          {payslip.status}
                        </Badge>
                      </td>
                      {/* Audit column */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {payslip.status === "FINALIZED" && payslip.finalizedBy ? (
                          <div className="flex items-center gap-1.5">
                            <UserCheck className="h-3.5 w-3.5 text-[#16a34a] shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-[#374151]">
                                {payslip.finalizedBy}
                              </p>
                              {payslip.finalizedAt && (
                                <p className="text-[10px] text-[#94a3b8]">
                                  {formatDate(payslip.finalizedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : wasOverridden ? (
                          <div>
                            <p className="text-xs font-medium text-[#d97706]">
                              {payslip.processedBy}
                            </p>
                            {payslip.processedAt && (
                              <p className="text-[10px] text-[#94a3b8]">
                                {formatDate(payslip.processedAt)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-[#94a3b8]">Auto-processed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {payslip.status === "DRAFT" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                setOverrideTarget({
                                  userId: payslip.userId,
                                  name,
                                })
                              }
                            >
                              <Edit3 className="h-3 w-3 mr-1" />
                              Override
                            </Button>
                          )}
                          {payslip.status === "FINALIZED" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownload(payslip)}
                              title="Download PDF"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Totals footer — always based on all payslips (not filtered) */}
              <tfoot>
                <tr className="border-t-2 border-[#e2e8f0] bg-[#f8fafc] font-semibold text-[#0f172a]">
                  <td className="px-4 py-3 text-xs uppercase tracking-wide text-[#64748b]">
                    Totals
                  </td>
                  <td className="px-4 py-3">
                    {formatCurrency(payslips.reduce((s, p) => s + p.basicPay, 0))}
                  </td>
                  <td className="px-4 py-3">
                    {formatCurrency(summary.totalGross)}
                  </td>
                  <td className="px-4 py-3 text-[#64748b]">
                    {formatCurrency(payslips.reduce((s, p) => s + getDeductionAmount(p.deductions, "SSS"), 0))}
                  </td>
                  <td className="px-4 py-3 text-[#dc2626]">
                    {formatCurrency(payslips.reduce((s, p) => s + getDeductionAmount(p.deductions, "Absence"), 0))}
                  </td>
                  <td className="px-4 py-3 text-[#7c3aed]">
                    {formatCurrency(summary.totalAdjustments)}
                  </td>
                  <td className="px-4 py-3 text-[#16a34a]">
                    {formatCurrency(payslips.reduce((s, p) => s + (p.incentives ?? []).reduce((is, d) => is + d.amount, 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-[#0891b2]">
                    {formatCurrency(payslips.reduce((s, p) => s + (p.cashAdvanceDeductions ?? []).reduce((cs, d) => cs + d.amount, 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-[#dc2626]">
                    {formatCurrency(summary.totalLoanDed)}
                  </td>
                  <td className="px-4 py-3 text-[#d97706]">
                    {formatCurrency(payslips.reduce((s, p) => s + (p.workAdjustments ?? []).reduce((ws, d) => ws + d.amount, 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-[#2447d9]">
                    {formatCurrency(summary.totalNet)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Override Modal */}
      {overrideTarget && (
        <Modal
          isOpen={!!overrideTarget}
          onClose={() => setOverrideTarget(null)}
          title={`Override Payslip — ${overrideTarget.name}`}
          size="md"
        >
          <ProcessPayslipModal
            userId={overrideTarget.userId}
            period={period}
            businessId={resolvedBusinessId || undefined}
            onClose={() => setOverrideTarget(null)}
            onSaved={() => refetchPayslips()}
          />
        </Modal>
      )}

      {/* Approve Confirmation Modal */}
      <Modal
        isOpen={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        title="Approve All Payslips"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-[#64748b]">
            This will finalize all <strong>{summary.draftCount}</strong> pending payslip
            {summary.draftCount !== 1 ? "s" : ""} for <strong>{periodLabel}</strong>. Once
            approved, employees will be able to view and download their payslips. This action
            cannot be undone.
          </p>
          {summary.totalLoanDed > 0 && (
            <div className="rounded-lg bg-[#eff3ff] border border-[#2447d9]/20 px-4 py-3 text-sm text-[#2447d9]">
              <strong>{formatCurrency(summary.totalLoanDed)}</strong> in loan deductions will be
              marked as collected.
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <Button variant="secondary" onClick={() => setConfirmApprove(false)} fullWidth>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApproveAll}
              fullWidth
              disabled={approvePayroll.isPending}
            >
              {approvePayroll.isPending ? "Approving…" : "Confirm Approve"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
